import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PrimeNgTablePageEvent, ScrBaseComponent } from 'src/app/components/base.component';
import { ComplaintReportRequest, ConsumerReportResponse, RiskType } from 'src/app/models/industry-reports.model';
import { TableColumn } from 'src/app/models/table-columns';
import { ComplaintSummary, IndustryReportsService } from 'src/app/services/industry-reports.service';
import { Router } from '@angular/router';
import { IuiMessagesComponent } from 'src/app/shared/messages/messages.component';
import { ScrBaseService } from 'src/app/services/base.service';
import { Table } from 'primeng/table';

export interface SummaryFilter {
  selection?: string;
  startDate?: Date;
  endDate?: Date;
}
@Component({
  selector: 'app-complaint-classification',
  templateUrl: './complaint-classification.component.html',
  styleUrls: ['./complaint-classification.component.scss']
})
export class ComplaintClassificationComponent extends ScrBaseComponent implements OnInit {

  @ViewChild('cccForm', { static: false }) form!: NgForm;
  @ViewChild('message', { static: false }) message!: IuiMessagesComponent;
  @ViewChild('complaintTable', { static: false }) complaintTable!: Table;
  today = new Date();
  cccRequest: ComplaintReportRequest = {};
  riskTypes: RiskType[] = [];
  showData = false;
  results: ConsumerReportResponse[] = [];
  
  summaryFilter: SummaryFilter = {};

  summaryData: ComplaintSummary[] = [];
  summaryColumns: TableColumn[] = [
    { field: 'riskType', width: '5' },
    { field: 'amount', width: '3' },
    { field: 'percentage', width: '3' },
  ];
  dateRanges = [
    { label: 'All', value: 'All' },
    { label: 'Previous Month', value: 'PreviousMonth' },
    { label: 'Year-to-Date', value: 'YearToDate' },
    { label: 'Previous 6 Months', value: 'Previous6Months' },
    { label: 'Previous Year', value: 'PreviousYear' },
    { label: 'Date Range', value: 'DateRange' },
  ];
  tableColumn: TableColumn[] = [
    { field: 'receivedDate', width: '3' },
    { field: 'shortCode', width: '5' },
    { field: 'riskTypes', width: '5' },
    { field: 'messageContent', width: '10' }
  ];
  totalRecords = 0;
  rowsInPage = 0;
  currentPage = 0;
  first = 0;

  selectionName: string | undefined;

  constructor(private industryReportsService: IndustryReportsService, private router: Router) {
    super();
  }

  ngOnInit(): void {
    this.getConfiguration();
    this.localizeTableColumns(this.tableColumn);
    this.localizeTableColumns(this.summaryColumns);
  }

  clearQuery(): void {
    this.clearMessages();
    this.summaryFilter = {
    };
  }

  summaryMinDate(): Date {
    return this.summaryFilter.startDate ?? new Date();
  }

  searchMinDate(): Date {
    return this.cccRequest.startDate ?? new Date();
  }

  onDownload(): void {
    this.industryReportsService.downloadComplaintClassification().subscribe({
      next: (res) => {
        this.industryReportsService.processBlobResponseDownload(res);
      },
      error: (error: HttpErrorResponse) => {
        this.processHttpScrErrorResponse(error); this.jumpToTop();
      }
    });
  }

  submitQuery(): void {
    this.clearMessages();
    this.summaryData = [];

    if (this.summaryFilter.selection !== 'DateRange') {
      delete this.summaryFilter.startDate;
      delete this.summaryFilter.endDate;
    }
    else if (this.summaryFilter.startDate && !this.summaryFilter.endDate) {
      this.displayLocalizedError('scr.endDateRequired', undefined, 'message');
      this.jumpToTop();
      return;
    }
    else if (!this.summaryFilter.startDate && !this.summaryFilter.endDate) {
      this.displayLocalizedError('scr.rangerequired', undefined, 'message');
      this.jumpToTop();
      return;
    }

    this.observe(this.industryReportsService.getComplaintSummary(this.summaryFilter.selection,
      this.summaryFilter.startDate ? this.summaryFilter.startDate.toISOString().split('T')[0] : undefined,
      this.summaryFilter.endDate ? this.summaryFilter.endDate.toISOString().split('T')[0] : undefined), (res => {

        this.selectionName = this.dateRanges.find(item => item.value === this.summaryFilter.selection)?.label;

        const today = new Date();

        const previousMonth = String(today.getMonth() + 1).padStart(2, '0');
        const previousYear = String(today.getFullYear()).padStart(4, '0');
        const today1 = new Date();

        const sevenMonthsBack = String(today1.getMonth() + 1).padStart(2, '0');
        const sevenMonthsBackYear = String(today1.getFullYear()).padStart(4, '0');

        if (this.summaryFilter.selection === undefined)
          this.selectionName = 'All';
        if (this.summaryFilter.selection === 'PreviousMonth')
          this.selectionName = `${previousMonth}/${previousYear}`;
        if (this.summaryFilter.selection === 'YearToDate')
          this.selectionName = `${String(new Date().getFullYear()).padStart(4, '0')}-to-Date`;
        if (this.summaryFilter.selection === 'Previous6Months')
          this.selectionName = `${sevenMonthsBack}/${sevenMonthsBackYear} - ${previousMonth}/${previousYear}`;
        if (this.summaryFilter.selection === 'PreviousYear')
          this.selectionName = `All ${String(new Date().getFullYear() - 1).padStart(4, '0')}`;

        this.summaryData = res.summary;
        return false;
      }));
  }

  onChangeSelection() {
    this.summaryFilter.startDate = undefined;
    this.summaryFilter.endDate = undefined;
  }

  getConfiguration() {
    this.industryReportsService.getRiskTypes().subscribe(res => {
      res.riskTypes.forEach(riskType => {
        this.riskTypes.push({ name: riskType, value: riskType.trim() });
      });
      this.cccRequest.riskTypes = this.riskTypes.map(rt => rt.value);

    }, (error: HttpErrorResponse) => { this.processHttpError(error); this.jumpToTop(); });
  }

  submit() {
    this.message.clearAll();
    if (this.cccRequest.startDate && this.cccRequest.endDate && this.cccRequest.startDate > this.cccRequest.endDate) {
      this.displayLocalizedError('scr.enddatestartdate.invalid', undefined, 'message');
      return;
    } else if (this.cccRequest.shortCode && this.isValidShortCode(this.cccRequest.shortCode)) {
      this.displayLocalizedError("scr.shortcode.invalid", undefined, 'message');
      return;
    } else if (this.cccRequest.startDate && !this.cccRequest.endDate) {
      this.displayLocalizedError("scr.endDateRequired", undefined, 'message');
      return;
    }

    this.fetchComplaintClassificationData(1);

    this.showData = true;
  }

  fetchComplaintClassificationData(pagenumber: number) {
    this.results = [];
    this.totalRecords = 0;
    this.rowsInPage = 0;
    const formData = Object.assign({} as ComplaintReportRequest,
      JSON.parse(JSON.stringify(this.cccRequest), this.prepareForSubmit));
    if (formData.startDate && !formData.endDate) {
      formData['endDate'] = '2100-01-01';
    }
    if (formData.endDate && !formData.startDate) {
      formData['startDate'] = '2019-01-01';
    }
    //formData['limit'] = 100;
    formData['page'] = pagenumber;
    // console.log(formData);
    if (this.cccRequest.shortCode) {
      formData['shortCode'] = ScrBaseService.shortCodeToNumeric(formData['shortCode']);
    }
    this.industryReportsService.fetchCccReports(formData).subscribe(res => {

      this.totalRecords = res.data.totalCount;
      this.rowsInPage = res.data.limit;
      this.results = res.data.content;
      if (pagenumber === 1) {
        this.complaintTable._first = 0;
      }
      window.scroll(0, 100);
    }, (error: HttpErrorResponse) => { this.processHttpError(error); this.jumpToTop(); });

  }

  paginateComplaint(event: PrimeNgTablePageEvent) {
    this.currentPage = (event.first ?? 0) / (event.rows ?? 0) + 1;
    this.fetchComplaintClassificationData(this.currentPage);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prepareForSubmit(key: string, value: any) {
    if ((key === 'startDate' || key === 'endDate') && value) {
      return new Date(value).toISOString().split('T')[0];
    }
    return value;
  }

  onReset() {
    this.form.resetForm();
    this.cccRequest = {};
    this.cccRequest.riskTypes = this.riskTypes.map(rt => rt.value);
  }

  isValidShortCode(shortCode: string): boolean {
    const sc = ScrBaseService.shortCodeToNumeric(shortCode);
    const start = sc.charAt(0);
    return sc.length < 5 || sc.length > 6 || start === "1" || start === "0";
  }

  navigateLookup(report: ConsumerReportResponse) {
    this.router.navigate(['/dashboard', { outlets: { 'navbar-tab': ['code-management', { outlets: { 'code-management-tab': ['code-lookup'] } }] } }], { state: { shortCode: report.shortCode } });
  }
}
